import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { verifyPayment } from '../services/pagosService';
import { getCommerceDetails } from '../services/comerciosService'; // Import the new service
import { useTranslation } from 'react-i18next';

const PaymentResultPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const sessionId = queryParams.get('session_id');
  const paymentCancelled = queryParams.get('payment_cancelled');
  const pedidoId = queryParams.get('pedido_id'); // Get pedido_id from query params
  const comercioKey = queryParams.get('comercio_key'); // Get comercio_key from query params

  const [message, setMessage] = useState<string>(t('paymentResultPage.processing'));
  const [isSuccess, setIsSuccess] = useState<boolean | null>(null);
  const [commerceName, setCommerceName] = useState<string | null>(null); // New state for commerce name

  useEffect(() => {
    const handlePaymentResult = async () => {
      if (paymentCancelled) {
        setMessage(t('paymentResultPage.cancelled'));
        setIsSuccess(false);
      } else if (sessionId && pedidoId && comercioKey) {
        try {
          // Placeholder for actual verification logic
          const response = await verifyPayment(comercioKey, pedidoId, sessionId); // Assuming this exists
          if (response.status === 'success') {
            setMessage(t('paymentResultPage.success'));
            setIsSuccess(true);

            // Fetch commerce name if payment is successful
            if (comercioKey) {
              try {
                const commerceDetails = await getCommerceDetails(comercioKey);
                setCommerceName(commerceDetails.nombre);
              } catch (commerceError) {
                console.error('Error fetching commerce details:', commerceError);
                setCommerceName(t('paymentResultPage.yourCommerce')); // Fallback
              }
            }
          } else {
            setMessage(t('paymentResultPage.verifyProblem'));
            setIsSuccess(false);
          }
        } catch (error) {
          console.error('Error verifying payment:', error);
          setMessage(t('paymentResultPage.verifyError'));
          setIsSuccess(false);
        }
      } else {
        setMessage(t('paymentResultPage.noInfo'));
        setIsSuccess(false);
      }
    };

    handlePaymentResult();
  }, [sessionId, paymentCancelled, pedidoId, comercioKey, t]);

  const getMessageClass = () => {
    if (isSuccess === true) return 'text-green-600';
    if (isSuccess === false) return 'text-red-600';
    return 'text-white/80';
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white/10 p-4">
      <div className="bg-black/20 p-8 rounded-lg shadow-lg text-center max-w-md w-full">
        {isSuccess === true && (
          <svg className="mx-auto mb-4 h-16 w-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        )}
        {isSuccess === false && (
          <svg className="mx-auto mb-4 h-16 w-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        )}
        <h1 className={`text-2xl font-bold mb-4 ${getMessageClass()}`}>
          {message}
        </h1>
        {isSuccess === true && (
          <div className="flex flex-col items-center mb-6">
            <p className="text-white/70 text-lg mb-2">
              {t('paymentResultPage.paidToWithPartnerPrefix', { commerceName: commerceName || t('paymentResultPage.yourCommerce') })}{' '}
              <Link to="https://taratic.com" className="text-blue-500 hover:underline">taratic.com</Link>
            </p>
            <Link to="https://taratic.com" target="_blank" rel="noopener noreferrer" className='flex items-center space-x-2'>
              <img src="https://storage.googleapis.com/taratic_website_bucket/website_images/LOGO_TARATIC_V8__solo_logo.svg" alt="Taratic Logo" className="h-12 mb-2" />
              <img src="https://storage.googleapis.com/taratic_website_bucket/website_images/LOGO_TARATIC_V8__solo_letras.svg" alt="Taratic Name" className="h-8" />
            </Link>
          </div>
        )}
        <p className="text-white/70 mb-6">
          {t('paymentResultPage.closeOrReturn')}
        </p>
        <button
          onClick={() => navigate('/')}
          className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-lg transition duration-300 ease-in-out"
        >
          {t('paymentResultPage.backHome')}
        </button>
      </div>
    </div>
  );
};

export default PaymentResultPage;
